// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — reconnect backoff scheduler (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// The §SM-13 reconnect policy (ASYMMETRIC wire):
//   • On a socket DROP (close that was NOT a deliberate disconnect AND NOT a
//     token-rotation 1008): ABORT any in-progress command.run turn (the client
//     fires an injected abort callback — the companion already failed it 503
//     `browser-disconnected-mid-request`, so the result is undeliverable and is
//     NEVER sent), clear the per-turn staged buffer hook, then BACKOFF and open
//     a FRESH socket → new hello → new welcome → NEW sessionEpoch. There is NO
//     resume / replay (INV-5) and NO browser-side outbound correlation map to
//     discard (that lives in the companion — the wire is asymmetric).
//   • On a companion token-rotation close (code 1008): NO auto-reconnect — the
//     token is dead; the client surfaces a re-prompt status and stops.
//
// This module owns ONLY the BACKOFF SCHEDULE (compute the next delay + arm a
// bounded timer + a cancel). The abort/clear/open-fresh actions are the client's
// (it owns the socket + the turn). Pure + injectable clock/timer so a test can
// drive time deterministically.
//
// LEAF module: imports NOTHING. No socket, no editorStore, no node-companion.
// ═══════════════════════════════════════════════════════════════════════════

/** Default first reconnect delay (ms). */
export const DEFAULT_RECONNECT_BASE_MS = 500;

/** Default reconnect delay ceiling (ms) — exponential backoff caps here. */
export const DEFAULT_RECONNECT_MAX_MS = 15_000;

/**
 * The WS close code the companion uses for ALL policy closes (`WS_CLOSE_POLICY`,
 * `wsStack.ts`:54). It is OVERLOADED across many reasons: a genuine auth failure
 * (`unauthorized`), MALFORMED-hello errors (`bad-hello`/`bad-document-id`/
 * `bad-document-epoch`/`malformed-frame`/`wrong-channel`/`binary-frame` — the
 * SAME hello/frame would deterministically fail again), AND the two RECOVERABLE
 * reasons a FRESH hello mints past — `lease-expired` (a valid-token tab that
 * missed a heartbeat) + `hello-deadline` (the socket simply didn't hello in time;
 * a fresh socket sends its hello immediately on open). The close REASON (not the
 * code) discriminates them.
 */
export const WS_CLOSE_TOKEN_ROTATED = 1008;

/**
 * [round-4 P2 #1] The browser `WebSocket.close(code)` API permits ONLY `1000`
 * (normal) or an application code in `3000`–`4999`; ANY OTHER code (the reserved
 * `1001`–`1015` range, e.g. `1002`/`1006`/`1008`/`1011`) makes `close()` THROW a
 * synchronous `InvalidAccessError`. A client-initiated close that THROWS is a real
 * bug: the handlers are already detached + `this.socket` nulled when it throws, so
 * the socket is NEVER actually closed → it lingers on the companion until heartbeat
 * lease-expiry, holding the active seat (forwarded commands then HANG instead of
 * failing closed). So EVERY client-initiated `close(code)` MUST use a browser-allowed
 * code.
 *
 *   • `WS_CLOSE_NORMAL` (1000) — a clean client teardown (`disconnect()`).
 *   • `WS_CLOSE_PROTOCOL_MISMATCH` (4002) — a distinct APPLICATION code (4xxx range,
 *     browser-allowed) for a protocol-version mismatch teardown. We do NOT use the
 *     reserved `1002` "protocol error" code (it would throw); a 4xxx app code carries
 *     the distinct meaning without the browser-API violation. The companion only ever
 *     sees this as a client-initiated close (it does not branch on the code), so the
 *     exact 4xxx value is informational.
 */
export const WS_CLOSE_NORMAL = 1000;

export const WS_CLOSE_PROTOCOL_MISMATCH = 4002;

/**
 * [round-4 P2 #1] Whether `code` is a browser-allowed `WebSocket.close()` code (`1000`
 * OR `3000`–`4999`). Every CLIENT-INITIATED close must pass this — a reserved code
 * (e.g. `1002`/`1006`/`1011`) throws synchronously, leaking the socket. Exposed so a
 * test can assert each close site uses an allowed code (and the codec-level contract is
 * documented next to the constants).
 */
export function isBrowserAllowedCloseCode(code: number): boolean {
  if (code === WS_CLOSE_NORMAL) {
    return true;
  }
  return Number.isInteger(code) && code >= 3000 && code <= 4999;
}

/**
 * The ONLY 1008 close REASONS a FRESH hello can recover (→ auto-reconnect):
 *   • `lease-expired` — a VALID-token tab that merely missed a heartbeat; a fresh
 *     hello mints a brand-new session ([wsServer.ts]:821).
 *   • `hello-deadline` — the socket failed to hello within the deadline; the new
 *     socket sends its hello synchronously on `open`, so it cannot re-trip it
 *     ([wsServer.ts]:622).
 *
 * EVERYTHING ELSE on a 1008 is NON-retryable (→ re-prompt, NO reconnect — a retry
 * with the SAME inputs would loop the SAME close):
 *   • `unauthorized` — a dead bearer token ([wsServer.ts]:709).
 *   • `bad-hello` — the hello FRAME is malformed; the client re-sends the SAME
 *     shape, so it deterministically re-fails ([wsServer.ts]:703).
 *   • `bad-document-id` / `bad-document-epoch` — the hello's document identity is
 *     malformed; a fresh hello reads the SAME live document identity → re-fails
 *     ([wsServer.ts]:721/730).
 *   • `malformed-frame` / `wrong-channel` / `binary-frame` — structural protocol
 *     defects this client never legitimately emits; deterministic → re-prompt
 *     ([wsServer.ts]:681/663/669).
 *
 * Captured from `wsServer.ts` (lines 622/663/669/681/703/709/721/730/821) on
 * 2026-06-22. INVERTED from round-1's "everything-but-unauthorized reconnects"
 * (which would infinite-loop a `bad-hello` with the same malformed hello — @codex
 * round-2 finding E): only a PROVABLY-recoverable reason reconnects, allow-listed.
 */
export const WS_CLOSE_RETRYABLE_REASONS = new Set<string>(["lease-expired", "hello-deadline"]);

/**
 * Whether a 1008 close REASON is RECOVERABLE by a fresh hello (→ auto-reconnect).
 * Only the explicit allow-list (`lease-expired` / `hello-deadline`) returns true;
 * an ABSENT/empty reason, an `unauthorized`, a `bad-hello`, or ANY other reason is
 * NON-retryable (a reason-less 1008 cannot be proven recoverable, so fail safe —
 * re-prompt rather than loop). The COMPLEMENT of this is "surface a re-prompt".
 */
export function is1008Retryable(reason: string | undefined): boolean {
  if (reason === undefined || reason.length === 0) {
    return false; // a reason-less 1008 → conservative re-prompt (never loop).
  }
  return WS_CLOSE_RETRYABLE_REASONS.has(reason);
}

export type ReconnectOptions = {
  baseMs?: number;
  maxMs?: number;
  /** Timer arm (default `setTimeout`). Injected so a test drives time. */
  setTimer?: (cb: () => void, ms: number) => ReturnType<typeof setTimeout>;
  /** Timer clear (default `clearTimeout`). Injected to pair with `setTimer`. */
  clearTimer?: (handle: ReturnType<typeof setTimeout>) => void;
};

/**
 * [round-5 P2] The SINGLE authority for the reconnect decision — `onClose`
 * DELEGATES the whole matrix here (it no longer pre-empts with its own pre-welcome
 * 1008 branch). The four inputs `(closeCode, wasDeliberate, reason, welcomed)` are
 * evaluated in a FIXED ORDER so the round-2 retryable allow-list and the round-4
 * welcomed-gating can NEVER collide (the round-4 bug: an onClose pre-welcome branch
 * treated EVERY pre-welcome 1008 as a token reject and returned BEFORE the allow-list
 * could fire — but `hello-deadline` is INHERENTLY pre-welcome (`wsServer.ts`:620-623
 * fires it ONLY while `state.paired === false`), so a valid tab that merely stalled
 * past the hello deadline was wrongly stopped + prompted for a token instead of
 * opening a fresh socket).
 *
 * The COMPLETE matrix (each cell verified against `wsServer.ts` close sites on
 * 2026-06-22 — `WS_CLOSE_POLICY` = 1008, `wsStack.ts`:54):
 *
 *   1. `wasDeliberate === true` → NO reconnect (the client tore the socket down).
 *   2. `reason ∈ WS_CLOSE_RETRYABLE_REASONS` (`hello-deadline` / `lease-expired`) →
 *      RECONNECT — checked BEFORE any code/welcomed gate. Both are TRANSIENT: a fresh
 *      hello mints a brand-new session (`lease-expired`, line 821, post-welcome) or
 *      the new socket sends its hello synchronously on open so it cannot re-trip the
 *      deadline (`hello-deadline`, line 622, ALWAYS pre-welcome). The retryability of
 *      the reason DOMINATES the welcomed flag — this is the round-5 fix.
 *   3. `closeCode === 1008` with a NON-retryable reason (`unauthorized` line 709,
 *      `bad-hello` 703, `bad-document-id` 721, `bad-document-epoch` 730,
 *      `malformed-frame` 681, `wrong-channel` 663, `binary-frame` 669, or a
 *      reason-less/unknown 1008) → NO reconnect: an auth/malformed-hello retry with
 *      the SAME inputs would loop the SAME close → surface token-rejected / re-prompt.
 *   4. NON-1008 close (1006/1011/…) AND `welcomed === false` (a FAILED INITIAL
 *      handshake — companion down / port refused → `error` then `close 1006`
 *      pre-welcome) → NO reconnect: surface companion-not-reachable rather than loop a
 *      doomed handshake (round-4 #2 intent).
 *   5. NON-1008 close AND `welcomed === true` (a normal post-pairing transient drop —
 *      network blip, companion restart without 1008) → RECONNECT.
 */
export function shouldAutoReconnect(
  closeCode: number,
  wasDeliberate: boolean,
  reason: string | undefined,
  welcomed: boolean,
): boolean {
  // 1. A deliberate disconnect always suppresses reconnect.
  if (wasDeliberate) {
    return false;
  }
  // 2. A provably-recoverable reason reconnects REGARDLESS of code or welcomed state.
  //    `hello-deadline` is inherently pre-welcome (welcomed === false) yet MUST
  //    reconnect — so this allow-list is checked BEFORE the welcomed gate below.
  if (is1008Retryable(reason)) {
    return true;
  }
  // 3. A 1008 with a non-retryable reason (auth reject / malformed-hello family /
  //    reason-less) is deterministic → NO reconnect (re-prompt instead).
  if (closeCode === WS_CLOSE_TOKEN_ROTATED) {
    return false;
  }
  // 4. A NON-1008 close that NEVER welcomed is a failed initial handshake → NO
  //    reconnect (surface companion-not-reachable, do not loop a doomed handshake).
  // 5. A NON-1008 close that DID welcome is a normal transient drop → RECONNECT.
  return welcomed;
}

/**
 * Exponential backoff scheduler for reconnect attempts. `schedule(fn)` arms a
 * single bounded timer at the current delay then advances the delay (×2, capped
 * at `maxMs`); `reset()` returns the delay to `baseMs` (called on a successful
 * welcome); `cancel()` clears any pending timer (called on deliberate disconnect
 * or a token-rotation close). Only ONE timer is ever outstanding.
 */
export class ReconnectScheduler {
  private readonly baseMs: number;

  private readonly maxMs: number;

  private readonly setTimer: NonNullable<ReconnectOptions["setTimer"]>;

  private readonly clearTimer: NonNullable<ReconnectOptions["clearTimer"]>;

  private currentDelayMs: number;

  private attempt = 0;

  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: ReconnectOptions = {}) {
    this.baseMs = options.baseMs ?? DEFAULT_RECONNECT_BASE_MS;
    this.maxMs = options.maxMs ?? DEFAULT_RECONNECT_MAX_MS;
    this.setTimer = options.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
    this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
    this.currentDelayMs = this.baseMs;
  }

  /** The delay (ms) the NEXT `schedule` will use (introspection / tests). */
  nextDelayMs(): number {
    return this.currentDelayMs;
  }

  /** The number of attempts scheduled since the last `reset` (introspection / tests). */
  attemptCount(): number {
    return this.attempt;
  }

  /**
   * Arm a single bounded timer at the current delay; on fire, run `fn` (the
   * client's "open a fresh socket" action). Advances the delay (×2, capped).
   * Cancels any prior pending timer first so only one is ever outstanding.
   */
  schedule(fn: () => void): void {
    this.cancel();
    const delay = this.currentDelayMs;
    this.attempt += 1;
    this.currentDelayMs = Math.min(this.currentDelayMs * 2, this.maxMs);
    this.timer = this.setTimer(() => {
      this.timer = null;
      fn();
    }, delay);
  }

  /** Reset the backoff to `baseMs` (called on a successful welcome). */
  reset(): void {
    this.currentDelayMs = this.baseMs;
    this.attempt = 0;
  }

  /** Clear any pending reconnect timer (deliberate disconnect / 1008). */
  cancel(): void {
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
  }
}
